import React, { useState, memo } from "react";
import { Check, ChevronsUpDown, Search, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Combobox de producto comercial reutilizable.
 * Permite escribir para filtrar por nombre, principio activo, marca, laboratorio o SKU.
 *
 * Props:
 *   value: id seleccionado
 *   onChange: (id) => void
 *   productos: lista [{id, nombre_comercial, principio_activo?, marca_nombre?, laboratorio_nombre?, codigo_interno?, iva_exento?}]
 *   placeholder: texto cuando no hay selección
 *   testId: data-testid prefix
 *   onCreateNew?: () => void  // si se provee, muestra un botón "+ Crear nuevo producto" al final
 */
function ProductoComboboxBase({ value, onChange, productos = [], placeholder = "Buscar producto…", testId = "producto-combo", className, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const selected = productos.find(p => p.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-9 justify-between font-normal text-left w-full", !selected && "text-[#8ba397]", className)}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate">
            {selected ? selected.nombre_comercial : placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(420px,calc(100vw-24px))] max-w-[420px] p-0" align="start">
        <Command shouldFilter={true} filter={(itemValue, search) => {
          const norm = (s) => (s || "").toLowerCase();
          return norm(itemValue).includes(norm(search)) ? 1 : 0;
        }}>
          <div className="flex items-center border-b border-[#d1dbd6] px-3" cmdk-input-wrapper="">
            <Search size={14} className="mr-2 shrink-0 opacity-50" />
            <CommandInput placeholder="Escriba para filtrar…" className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-[#8ba397] disabled:cursor-not-allowed disabled:opacity-50" data-testid={`${testId}-input`} />
          </div>
          <CommandList className="max-h-[320px] overflow-y-auto">
            <CommandEmpty className="py-4 text-center text-sm text-[#8ba397]">
              <div>Sin resultados</div>
              {onCreateNew && (
                <button
                  type="button"
                  className="mt-3 inline-flex items-center gap-1 text-[#205c45] font-semibold underline-offset-2 hover:underline"
                  onClick={() => { setOpen(false); onCreateNew(); }}
                  data-testid={`${testId}-create-empty`}
                >
                  <Plus size={14} /> Crear un producto nuevo
                </button>
              )}
            </CommandEmpty>
            <CommandGroup>
              {productos.map(p => {
                const searchable = [
                  p.nombre_comercial, p.principio_activo, p.marca_nombre,
                  p.laboratorio_nombre, p.codigo_interno
                ].filter(Boolean).join(" ");
                const stock = Number(p.stock_total ?? 0);
                const venc = p.proxima_vencimiento;
                const stockColor = stock <= 0 ? "text-red-600" : stock < 10 ? "text-amber-600" : "text-[#205c45]";
                let vencColor = "text-[#5c7368]";
                if (venc) {
                  const dias = Math.floor((new Date(venc).getTime() - Date.now()) / 86400000);
                  if (dias <= 0) vencColor = "text-red-600 font-semibold";
                  else if (dias <= 90) vencColor = "text-amber-700 font-semibold";
                }
                return (
                  <CommandItem
                    key={p.id}
                    value={searchable}
                    onSelect={() => { onChange(p.id); setOpen(false); }}
                    data-testid={`${testId}-item-${p.id}`}
                  >
                    <Check size={14} className={cn("mr-2 mt-1 shrink-0", value === p.id ? "opacity-100 text-[#205c45]" : "opacity-0")} />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-medium leading-snug break-words whitespace-normal">{p.nombre_comercial}</span>
                      <span className="text-[11px] text-[#5c7368] leading-snug break-words whitespace-normal">
                        {[
                          p.laboratorio_nombre || (<span key="lab-na" className="italic opacity-70">Lab. n/d</span>),
                          p.marca_nombre,
                          p.codigo_interno,
                        ].filter(Boolean).map((v, i) => (
                          <React.Fragment key={i}>{i > 0 ? " · " : ""}{v}</React.Fragment>
                        ))}
                        {p.iva_exento ? <span className="text-[#205c45] ml-1">· Exento</span> : null}
                      </span>
                      <span className="text-[11px] flex items-center gap-2 mt-0.5">
                        <span className={cn("font-semibold", stockColor)} data-testid={`${testId}-stock-${p.id}`}>
                          Stock {stock}
                        </span>
                        {Number(p.stock_reservado || 0) > 0 && (
                          <span className="text-[#8a5500]" data-testid={`${testId}-reservado-${p.id}`}>
                            · {p.stock_reservado} en otras NP
                          </span>
                        )}
                        <span className={vencColor} data-testid={`${testId}-venc-${p.id}`}>
                          {venc ? `· Vence ${venc}` : "· Sin lotes"}
                        </span>
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {onCreateNew && (
              <div className="border-t border-[#d1dbd6] p-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-[#205c45] hover:bg-[#e9f2ee] hover:text-[#205c45] font-semibold"
                  onClick={() => { setOpen(false); onCreateNew(); }}
                  data-testid={`${testId}-create-footer`}
                >
                  <Plus size={14} className="mr-2" />
                  Crear nuevo producto comercial
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const ProductoCombobox = memo(ProductoComboboxBase, (prev, next) => (
  prev.value === next.value &&
  prev.productos === next.productos &&
  prev.placeholder === next.placeholder &&
  prev.testId === next.testId &&
  prev.className === next.className &&
  prev.onCreateNew === next.onCreateNew
));
